import { createClient } from "@supabase/supabase-js";

let supabaseClient = null;

const API_VIEW_NAME_OVERRIDES = {
  client_error_reports: "v_client_error_reports",
};

const API_RPC_NAMES = new Set([
  "accept_invite",
  "add_conversation_members",
  "archive_hierarchy",
  "auth_resolve",
  "change_history_page",
  "change_history_users",
  "claim_enterprise_contract_owner",
  "claim_enterprise_contract_owner_for_user",
  "conversation_members",
  "conversation_messages",
  "conversation_messages_search",
  "conversations_inbox",
  "conversations_search",
  "create_conversation",
  "create_invite",
  "create_team",
  "delete_team",
  "delete_hierarchy",
  "ensure_org_contract",
  "get_invite_public",
  "log_event",
  "mark_conversation_read",
  "move_team_member",
  "org_seat_count",
  "pins_detailed",
  "post_conversation_message",
  "profile_lookup_by_email_ci",
  "restore_hierarchy",
  "remove_team_member",
  "rename_team",
  "revoke_invite",
  "save_annotations_batch",
  "search_items",
  "session_context",
  "set_org_member_role",
  "set_team_member_role",
  "tag_candidates",
  "team_activity",
  "toggle_conversation_message_reaction",
]);

function getApiSchemaClient(client) {
  if (typeof client?.schema !== "function") {
    return client;
  }
  return client.schema("api");
}

function getApiViewName(name) {
  if (typeof name !== "string" || !name) return null;
  if (API_VIEW_NAME_OVERRIDES[name]) return API_VIEW_NAME_OVERRIDES[name];
  if (name.startsWith("v_")) return name;
  return null;
}

function shouldUseApiRpc(name) {
  if (typeof name !== "string" || !name) return false;
  return API_RPC_NAMES.has(name) || name.startsWith("admin_");
}

function createSupabaseClient() {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const missingEnv = [];
  if (!supabaseUrl) missingEnv.push("REACT_APP_SUPABASE_URL");
  if (!supabaseKey) missingEnv.push("REACT_APP_SUPABASE_ANON_KEY");
  if (missingEnv.length) {
    const message = `Missing Supabase env vars: ${missingEnv.join(", ")}.`;
    // eslint-disable-next-line no-console
    console.warn(message);
    throw new Error(message);
  }

  return createClient(supabaseUrl, supabaseKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
}

export function getSupabaseClient() {
  if (!supabaseClient) {
    supabaseClient = createSupabaseClient();
  }
  return supabaseClient;
}

export function setSupabaseClientForTests(client) {
  supabaseClient = client;
}

export function resetSupabaseClientForTests() {
  supabaseClient = null;
}

export const supabase = new Proxy(
  {},
  {
    get(_target, prop) {
      const client = getSupabaseClient();
      if (prop === "from") {
        return (name) => {
          const apiViewName = getApiViewName(name);
          if (apiViewName) {
            const apiClient = getApiSchemaClient(client);
            if (typeof apiClient?.from === "function") {
              return apiClient.from(apiViewName);
            }
          }
          return client.from(name);
        };
      }
      if (prop === "rpc") {
        return (name, args) => {
          if (shouldUseApiRpc(name)) {
            const apiClient = getApiSchemaClient(client);
            if (typeof apiClient?.rpc === "function") {
              return apiClient.rpc(name, args);
            }
          }
          return client.rpc(name, args);
        };
      }
      const value = client[prop];
      if (typeof value === "function") {
        return value.bind(client);
      }
      return value;
    },
  },
);
